import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Loader2, FolderPlus } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import OspiteSearchSelect from "./OspiteSearchSelect";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEZIONI = [
  { value: "generale", label: "Generale" },
  { value: "anagrafica", label: "Anagrafica" },
  { value: "amministrativa", label: "Amministrativa" },
  { value: "sociale", label: "Sociale" },
  { value: "sanitaria", label: "Sanitaria" },
  { value: "terapie_ausili", label: "Terapie e Ausili (allegati)" },
  { value: "richiesta_rsa", label: "Richiesta RSA" },
  { value: "dietetica", label: "Tabella Dietetica" },
];

const ALLOWED_TYPES = [".pdf", ".jpg", ".jpeg", ".png", ".gif", ".doc", ".docx", ".xls", ".xlsx"];

export default function UploadFascicoloDocumentDialog({ open, onOpenChange }) {
  const { token } = useAuth();
  const [ospiti, setOspiti] = useState([]);
  const [ospiteId, setOspiteId] = useState("");
  const [sezione, setSezione] = useState("generale");
  const [descrizione, setDescrizione] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open || !token) return;
    axios.get(`${API_URL}/api/fascicoli-assistenziali`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => setOspiti(res.data || []))
      .catch(() => toast.error("Errore nel caricamento degli ospiti"));
  }, [open, token]);

  const reset = () => {
    setOspiteId("");
    setSezione("generale");
    setDescrizione("");
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ospiteId) {
      toast.error("Seleziona un ospite");
      event.target.value = "";
      return;
    }

    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File troppo grande. Massimo 50 MB");
      event.target.value = "";
      return;
    }

    const ext = `.${file.name.split(".").pop().toLowerCase()}`;
    if (!ALLOWED_TYPES.includes(ext)) {
      toast.error(`Tipo file non supportato. Formati ammessi: ${ALLOWED_TYPES.join(", ")}`);
      event.target.value = "";
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("descrizione", descrizione);

    try {
      const response = await axios.post(
        `${API_URL}/api/upload/cartella/${ospiteId}/${sezione}`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      if (response.data.success) {
        toast.success("Documento caricato con successo");
        handleClose(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderPlus className="w-5 h-5 text-purple-600" />
            Aggiungi documento fascicolo ospite
          </DialogTitle>
          <DialogDescription>
            Seleziona l'ospite e carica un documento nel fascicolo assistenziale.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Ospite *</Label>
            <OspiteSearchSelect ospiti={ospiti} value={ospiteId} onChange={setOspiteId} />
          </div>

          <div className="space-y-2">
            <Label>Sezione di appartenenza</Label>
            <Select value={sezione} onValueChange={setSezione}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {SEZIONI.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Descrizione (opzionale)</Label>
            <Input
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              placeholder="Es: Carta d'identità, Certificato medico..."
            />
          </div>

          <div className="space-y-2">
            <Label>File *</Label>
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
              <input
                type="file"
                accept={ALLOWED_TYPES.join(",")}
                onChange={handleUpload}
                className="hidden"
                id="quick-upload-file"
                disabled={uploading || !ospiteId}
              />
              <label htmlFor="quick-upload-file" className={`cursor-pointer ${!ospiteId ? "opacity-50 cursor-not-allowed" : ""}`}>
                {uploading ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="w-8 h-8 text-purple-600 animate-spin mb-2" />
                    <span className="text-sm text-slate-600">Caricamento in corso...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="w-8 h-8 text-slate-400 mb-2" />
                    <span className="text-sm text-slate-600">Clicca per selezionare un file</span>
                    <span className="text-xs text-slate-400 mt-1">PDF, JPG, PNG, DOC, XLS (max 50 MB)</span>
                  </div>
                )}
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={uploading}>Annulla</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
